/**
 * SI is the sim's internal unit system (AD-007c); every value that reaches a
 * cockpit gauge or a `.DAT` field goes through one of these conversions at the
 * boundary. The raw-angular-unit constant is the one conversion that is itself
 * a calibration target — see `docs/engineering/flight-tunables.md` §2.1.
 */

/** Sim tick length. AD-007a fixes the physics rate at 60 Hz, decoupled from render. */
export const FIXED_DT = 1 / 60;

export const METRES_PER_FOOT = 0.3048;
export const MPS_PER_MPH = 0.44704;
export const GRAVITY_MPS2 = 9.80665;
/** ISA sea-level air density, the reference the drag polar is calibrated at. */
export const SEA_LEVEL_DENSITY_KGM3 = 1.225;

/** BAM16: the `.DAT` gun-cone unit where a full turn is 65536 counts (flight-tunables.md §6). */
export const BAM16_PER_TURN = 65536;

export function feetToMetres(feet: number): number {
  return feet * METRES_PER_FOOT;
}

export function metresToFeet(metres: number): number {
  return metres / METRES_PER_FOOT;
}

export function mphToMps(mph: number): number {
  return mph * MPS_PER_MPH;
}

export function mpsToMph(mps: number): number {
  return mps / MPS_PER_MPH;
}

export function degreesToRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

export function radiansToDegrees(radians: number): number {
  return (radians * 180) / Math.PI;
}

/** Converts a BAM16 field-of-fire angle to radians (65536 counts = 2π). */
export function bamToRadians(bam: number): number {
  return (bam / BAM16_PER_TURN) * 2 * Math.PI;
}

/**
 * Converts one of the `.DAT` angular-rate fields (rudder yaw 54, turn
 * performance 116/118) to rad/s. The ÷30 deg/s constant is REPORTED, from
 * J.W. Johnson's stopwatched full-rudder 360° yaws in a Nieuport 17
 * (flight-tunables.md §2.1) and is good to about ±10 %, so it is passed in as
 * a tunable rather than hardcoded here.
 */
export function rawRateToRadPerSecond(raw: number, degPerSecondPerCount: number): number {
  return degreesToRadians(raw * degPerSecondPerCount);
}

export function clamp(value: number, low: number, high: number): number {
  if (value < low) return low;
  if (value > high) return high;
  return value;
}

/** Signum that treats exact zero as zero (used for turn/spin direction sign tests). */
export function signOf(value: number): number {
  if (value > 0) return 1;
  if (value < 0) return -1;
  return 0;
}
